// src/receptionist/components/ui.tsx
//
// The house pieces the receptionist screens compose from, drawn in the Oura
// look (ourteam-frontend#4785): filter pills with a dot and a count, toned
// tags, pill buttons and sentence-case headings, as the @oura/ui kit draws
// them. Inputs stay the shared form components (@component/*), which the kit
// does not replace. Cards, page heads and buttons are in ./kit.

import type { ReactNode } from "react";
import { CalendarIcon, ClockIcon, PlusIcon } from "lucide-react";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { DOT_TONE, FOCUS_RING, TAG_TONE, TYPE, type Tone } from "@oura/ui";
import { cn } from "@oura/lib/cn";

/**
 * A field heading: icon, title, optional required mark and hint, then the
 * subtitle underneath. Matches the assessment forms
 * (containers/asess-section/interview-setting-section-v2.tsx), which is where
 * this treatment is established.
 */
export function SectionLabel({
    icon: Icon,
    title,
    required,
    subtitle,
    aside,
    htmlFor,
}: {
    icon?: React.ComponentType<{ className?: string }>;
    title: string;
    required?: boolean;
    subtitle?: ReactNode;
    /** Right-aligned control on the title row — a switch, a chip. */
    aside?: ReactNode;
    htmlFor?: string;
}) {
    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <Label htmlFor={htmlFor} className={cn(TYPE.tb, "flex items-center gap-2 font-medium text-gray-900")}>
                    {Icon && <Icon className="h-4 w-4 shrink-0 text-blue-600" />}
                    <span>
                        {title}
                        {required && <span className="ml-0.5 text-red-500">*</span>}
                    </span>
                </Label>
                {aside && <span className="ml-auto flex items-center">{aside}</span>}
            </div>
            {subtitle && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{subtitle}</p>}
        </div>
    );
}

/**
 * One WhatsApp bubble, on the app's own chat ground.
 *
 * Same treatment as the hiring flow's message preview
 * (containers/hiring-flow-builder.tsx): the #ECE5DD chat paper, a white
 * incoming bubble with its top-left corner squared off, and a timestamp. A
 * merchant writing a message should see the thing the customer will see, not
 * a grey rectangle.
 */
export function WaPreview({
    text,
    placeholder = "Nothing written yet",
    note,
}: {
    text: string;
    placeholder?: string;
    /** A system line above the bubble, e.g. "Sent when your team confirms". */
    note?: string;
}) {
    const body = text.trim();
    return (
        <div className="rounded-lg bg-[#ECE5DD] px-3 py-3 dark:bg-[#0B141A]">
            {note && (
                <div className="mb-2 flex justify-center">
                    <span className="rounded-md bg-white/90 px-2 py-0.5 text-[10px] text-gray-500 shadow-sm dark:bg-[#202C33] dark:text-gray-300">
                        {note}
                    </span>
                </div>
            )}
            <div className="max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm dark:bg-[#202C33]">
                <p className={cn(
                    "whitespace-pre-wrap break-words text-sm",
                    body ? "text-gray-800 dark:text-gray-100" : "italic text-gray-400",
                )}>
                    {body || placeholder}
                </p>
                <p className="mt-1 text-right text-[10px] text-gray-400">09:41</p>
            </div>
        </div>
    );
}

/** A toned tag, the Oura `Tag`: tinted ground, 1px inset ring. Blue unless a tone is given. */
export function TagPill({ children, className, tone = "blue" }: { children: ReactNode; className?: string; tone?: Tone }) {
    return (
        <span className={cn(
            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
            TAG_TONE[tone],
            className,
        )}>
            {children}
        </span>
    );
}

/** An inline action in a row: blue, underlined on hover, no icon. */
export function RowAction({
    onClick,
    children,
    className,
}: {
    onClick: () => void;
    children: ReactNode;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn("text-sm font-medium text-blue-600 transition-colors hover:underline", className)}
        >
            {children}
        </button>
    );
}

/** "Add another": the Oura secondary pill with a plus. */
export function AddButton({
    onClick,
    children,
    disabled,
}: {
    onClick: () => void;
    children: ReactNode;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
                TYPE.tc,
                "inline-flex h-8 items-center gap-1.5 rounded-full bg-white px-4 font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40",
                FOCUS_RING,
            )}
        >
            <PlusIcon aria-hidden className="h-3.5 w-3.5" />
            {children}
        </button>
    );
}

/**
 * Views of one page (the bookings queue, the flow's parts), as Oura's status
 * filter draws them: a row of pills, each with an optional dot and a count,
 * the active one filled ink. Every option shows, a zero count included, so
 * the row does not change shape as the queue moves.
 */
export function FilterTabs<T extends string>({
    value,
    onChange,
    options,
}: {
    value: T;
    onChange: (next: T) => void;
    options: { value: T; label: string; count?: number; icon?: React.ComponentType<{ className?: string }>; tone?: Tone }[];
}) {
    return (
        <div role="tablist" className="flex flex-wrap gap-2">
            {options.map(({ value: v, label, count, tone }) => {
                const on = value === v;
                return (
                    <button
                        key={v}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => onChange(v)}
                        className={cn(
                            TYPE.tc,
                            "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium transition-colors sm:h-[30px]",
                            on ? "bg-gray-900 text-white" : "bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                            FOCUS_RING,
                        )}
                    >
                        {tone && <span aria-hidden className={cn("h-2 w-2 rounded-full", DOT_TONE[tone])} />}
                        {label}
                        {count !== undefined && <span className={cn("tabular-nums", on ? "text-white/70" : "text-gray-400")}>{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

/** Oura Settings' own tab (containers/setting-v2.tsx, isOura), class for class. */
const PAGE_TAB = "text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap";
const PAGE_TAB_PAD = { paddingTop: "8px", paddingRight: "24px", paddingBottom: "12px", paddingLeft: "24px" } as const;

/**
 * The sections of one page, as Oura Settings draws them: underlined tabs with
 * an icon, the active one blue. One look for every receptionist page that
 * switches sections (Settings, Conversation), so no two pages differ.
 */
export function PageSectionTabs<T extends string>({
    value,
    onChange,
    options,
}: {
    value: T;
    onChange: (next: T) => void;
    options: { value: T; label: string; count?: number; icon?: React.ComponentType<{ className?: string }> }[];
}) {
    return (
        <Tabs value={value} onValueChange={(v) => onChange(v as T)} className="w-full rounded-none">
            <TabsList className="mb-8 flex h-auto w-full justify-start overflow-x-auto rounded-none border-b border-gray-200 bg-transparent p-0 scrollbar-hide">
                {options.map(({ value: v, label, count, icon: Icon }) => (
                    <TabsTrigger key={v} value={v} className={PAGE_TAB} style={PAGE_TAB_PAD}>
                        {Icon && <Icon className="h-4 w-4" />}
                        {label}
                        {count !== undefined && <span className="tabular-nums text-gray-400">{count}</span>}
                    </TabsTrigger>
                ))}
            </TabsList>
        </Tabs>
    );
}

/**
 * A time or date field with its icon at the right edge (29 Oct 2026). The
 * browser's own picker icon sat right after the text, mid-field. It is now
 * invisible and stretched under our Lucide icon, so clicking the icon still
 * opens the native picker, and the field reads like every other input.
 */
export function PickerInput({
    kind,
    className,
    ...rest
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { kind: "time" | "date" }) {
    const Icon = kind === "time" ? ClockIcon : CalendarIcon;
    return (
        <span className={cn("relative flex min-w-0", className)}>
            <Input
                type={kind}
                {...rest}
                className={cn(
                    "w-full pr-10 tabular-nums",
                    "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0",
                    "[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0",
                )}
            />
            <Icon aria-hidden className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        </span>
    );
}
